/**
 * "Ponovno uključi jedinice": offered only right after an outcome that is
 * KNOWN — the change was refused, renewal must be turned on first, the host
 * cancelled the re-preview / Stripe Checkout (?cancelled, Back) or the
 * scheduled smaller plan. Never after an unknown outcome (timeout, broken
 * connection), and never on its own on a page. Shown to hosts with full access and to
 * hosts in the 30-day grace (the server lets them switch units back on up to
 * the old cap); a blocked host is not told to do something the server would
 * refuse.
 *
 * The memory is keyed by the Clerk user id. After a click it says exactly
 * what happened: all on / some on and the rest deleted meanwhile / some
 * still off (retry offered) / plan full.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui';
import type { BillingInfo } from '@/hooks/useSubscription';
import { accessLevel } from '@/lib/billingAccess';
import { dbBillingCode } from '@/lib/billingErrors';
import { readDeactivatedUnits } from '@/lib/flowDeactivatedUnits';
import { switchUnitsBackOn } from './reEnableUnits';

type OfferState = 'idle' | 'working' | 'partial' | 'full' | 'error' | 'done' | 'doneSomeDeleted' | 'allDeleted';

export function ReEnableUnitsOffer({ billing }: { billing: BillingInfo | null | undefined }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useUser();
  const userId = user?.id ?? null;
  const [ids, setIds] = useState(() => readDeactivatedUnits(userId));
  const [state, setState] = useState<OfferState>('idle');
  const [counts, setCounts] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  // The user may arrive after the first render.
  useEffect(() => {
    if (state === 'idle') setIds(readDeactivatedUnits(userId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (state === 'done' || state === 'doneSomeDeleted' || state === 'allDeleted') {
    return (
      <p className="text-sm text-green-700 dark:text-green-300" data-testid="units-reenabled" data-result={state} role="status">
        {state === 'done'
          ? t('billing.reenable.done', 'Jedinice su ponovno aktivne.')
          : state === 'doneSomeDeleted'
            ? t('billing.reenable.doneSomeDeleted', {
                done: counts.done,
                total: counts.total,
                defaultValue: 'Ponovno uključeno {{done}} od {{total}} — ostale su u međuvremenu obrisane.',
              })
            : t('billing.reenable.allDeleted', 'Te jedinice su u međuvremenu obrisane — nema što uključiti.')}
      </p>
    );
  }

  if (!ids.length || !billing || accessLevel(billing) === 'blocked') return null;

  const handleClick = async () => {
    setState('working');
    try {
      const out = await switchUnitsBackOn(userId, ids, queryClient);
      setCounts({ done: out.changed.length, total: ids.length });
      setIds(out.stillOff);
      setState(
        out.stillOff.length > 0
          ? 'partial'
          : out.missing.length === 0
            ? 'done'
            : out.changed.length > 0
              ? 'doneSomeDeleted'
              : 'allDeleted',
      );
    } catch (e) {
      setState(dbBillingCode(e) === 'UNIT_LIMIT' ? 'full' : 'error');
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2" data-testid="reenable-units" data-state={state}>
      <p className="text-sm flex-1">
        {state === 'full'
          ? t('billing.reenable.full', 'Plan nema mjesta za sve te jedinice — uključi ih pojedinačno na stranici jedinice.')
          : state === 'error'
            ? t('billing.reenable.error', 'Uključivanje nije uspjelo. Pokušaj ponovno.')
            : state === 'partial'
              ? t('billing.reenable.partial', {
                  done: counts.done,
                  total: counts.total,
                  defaultValue: 'Ponovno uključeno {{done}} od {{total}}. Ostale još nisu uključene — pokušaj ponovno.',
                })
              : t('billing.reenable.body', {
                  count: ids.length,
                  defaultValue: 'Jedinice koje si isključio za promjenu plana ({{count}}) možeš ponovno uključiti.',
                })}
      </p>
      {state !== 'full' && (
        <Button size="sm" variant="outline" onClick={handleClick} disabled={state === 'working'} className="gap-2">
          {state === 'working' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
          {state === 'partial' || state === 'error'
            ? t('billing.reenable.retry', 'Pokušaj ponovno')
            : t('billing.reenable.button', 'Ponovno uključi jedinice')}
        </Button>
      )}
    </div>
  );
}

export default ReEnableUnitsOffer;
